// Selector.tsx
import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';

interface Option {
    value: string;
    label: string;
}

interface SelectorProps {
    options: Option[];
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    error?: boolean;
    id?: string;
    name?: string;
    required?: boolean;
    className?: string;
}

const Selector: React.FC<SelectorProps> =
    ({
         options,
         value, onChange,
         placeholder = "Оберіть варіант",
         error = false,
         id = "selector",
         name,
         required = false,
         className = ""
     }) => {
        const [isOpen, setIsOpen] = useState(false);
        const [dropdownPosition, setDropdownPosition] = useState<'bottom' | 'top'>('bottom');
        const selectRef = useRef<HTMLDivElement>(null);
        const dropdownRef = useRef<HTMLDivElement>(null);

        const selectedOption = options.find(option => option.value === value);

        useEffect(() => {
            const handleClickOutside = (event: MouseEvent) => {
                if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
                    setIsOpen(false);
                }
            };
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }, []);

        useEffect(() => {
            if (isOpen && selectRef.current) {
                const rect = selectRef.current.getBoundingClientRect();
                const viewportHeight = window.innerHeight;
                const dropdownHeight = 200;
                if (rect.bottom + dropdownHeight > viewportHeight && rect.top > dropdownHeight) {
                    setDropdownPosition('top');
                } else {
                    setDropdownPosition('bottom');
                }
            }
        }, [isOpen]);

        const handleToggle = () => {
            setIsOpen(!isOpen);
        };

        const handleSelect = (optionValue: string) => {
            onChange(optionValue);
            setIsOpen(false);
        };

        const handleKeyDown = (event: React.KeyboardEvent) => {
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                handleToggle();
            } else if (event.key === 'Escape') {
                setIsOpen(false);
            }
        };

        return (
            <div className="relative" ref={selectRef}>
                <div
                    role="combobox"
                    aria-label="Оберіть варіант"
                    tabIndex={0}
                    aria-haspopup="listbox"
                    aria-expanded={isOpen}
                    aria-controls={`${id}-listbox`}
                    aria-required={required}
                    aria-invalid={error}
                    aria-describedby={error ? `${id}-error` : undefined}
                    onClick={handleToggle}
                    onKeyDown={handleKeyDown}
                    className={`font-light w-full px-4 py-3 bg-black/50 border rounded-lg text-white cursor-pointer transition-colors focus:outline-none focus:ring-1 focus:ring-gray-900 focus:ring-offset-1 focus:ring-offset-gray-900 ${
                        error ? "border-red-500" : "border-gray-700 focus:border-white"
                    } ${!value ? "text-gray-400" : "text-white"} ${className}`}>
                    <div className="flex items-center justify-between">
                        <span>{selectedOption ? selectedOption.label : placeholder}</span>
                        <ChevronDown
                            className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${
                                isOpen ? 'rotate-180' : ''
                            }`}
                        />
                    </div>
                </div>
                {isOpen && (
                    <div
                        id={`${id}-listbox`}
                        role="listbox"
                        ref={dropdownRef}
                        className={`absolute left-0 right-0 z-50 bg-black border border-gray-700 rounded-lg shadow-lg max-h-48 overflow-y-auto ${
                            dropdownPosition === 'top' ? 'bottom-full mb-1' : 'top-full mt-1'
                        }`}>
                        {options
                            .filter((option) => option.value !== "")
                            .map((option) => (
                                <div
                                    id={`${id}-option-${option.value}`}
                                    role="option"
                                    key={option.value}
                                    aria-selected={option.value === value}
                                    onClick={() => handleSelect(option.value)}
                                    className={`px-4 py-3 text-light text-sm text-white cursor-pointer transition-colors hover:bg-gray-800 ${
                                        option.value === value ? "bg-gray-800" : ""
                                    }`}>
                                    {option.label}
                                </div>
                            ))}
                    </div>
                )}
                <select
                    id={id}
                    name={name}
                    value={value}
                    onChange={() => { }}
                    required={required}
                    className="sr-only"
                    tabIndex={-1}>
                    {options.map((option) => (
                        <option key={option.value} value={option.value} disabled={option.value === ""}>
                            {option.label}
                        </option>
                    ))}
                </select>
            </div>
        );
    };

export default Selector;
